// Internal modules
import { appStore } from '@/store';
import {
  CI,
  features,
  FIELD_LEVEL_ERRORS,
  focusOnFirstError,
  getCheckoutSessionFlags,
  getToken,
  getWorldReadyIdentityDocumentErrorPayload,
  handleApiError,
  logger,
  PAGE_LEVEL_ERRORS,
  parseGqlError,
} from '@/lib';
import { threeDSHandler } from '../threeDSHandler';
import {
  getErrorFieldAndPayload,
  triggerErrorBasedSpecificActions,
} from '../utils';
import { ADDRESS_TYPES } from '../../Address/constants';
import { getErrorMessageByAddressType } from '../../Interstitials/ResidentialAddress/utils';
import { logPrepareResidentialAddressInterstitialToFPTI } from '../../Interstitials/ResidentialAddress/fpti';
import {
  atomics,
  atomicsDomain,
  atomicsErrorReason,
  atomicsStatus,
} from '@/lib/atomics';

/**
 * @function handleResidentialAddressContingency
 * @description Handles the ResidentialAddress contingency,
 *  updating the state accordingly
 * @param {object} errorData Data of the GQL error
 */
const handleResidentialAddressContingency = errorData => {
  // Add the error message to the line 1 of the Residential Address
  const updateAddressPayload = {
    addressType: ADDRESS_TYPES.RESIDENTIAL_ADDRESS,
    isBatchUpdate: true,
    payload: {
      line1: {
        displayError: true,
        error: getErrorMessageByAddressType(errorData.addressType),
        errorCode: 'RESIDENTIAL_ADDRESS_INVALID',
        isValid: false,
      },
    },
  };
  const { form: formActions, ui: uiActions } = appStore.getActions();

  formActions.updateAddressField(updateAddressPayload);
  // Set if "Save" of Residential Address interstitial should submit or not
  // It'll call PayNow or PayLater handlers
  //  according to the components it's inside
  formActions.updateResidentialAddressButtonShouldSubmit(true);

  // Open interstitial
  uiActions.toggleIsResidentialAddressOpen(true);

  logPrepareResidentialAddressInterstitialToFPTI({
    addressType: errorData.addressType,
    invalidAddressContingency: true,
  });
};

const handlePOBoxLine1AddressContingency = errorData => {
  // Add the error message to the line 1 of the Billing/Shipping Address
  errorData.forEach(item => {
    const type = item?.addressType;
    const updateAddressPayload = {
      addressType: type,
      isBatchUpdate: true,
      payload: {
        line1: {
          displayError: true,
          error: getErrorMessageByAddressType(type),
          errorCode: `${type}_INVALID`,
          isValid: false,
        },
      },
    };

    const { form: formActions } = appStore.getActions();

    formActions.updateAddressField(updateAddressPayload);
  });
};

const onboardErrorHandler = err => {
  const {
    error,
    errorData,
    isFiConfirmationContingency,
    isOasError,
    isOpenBankingTerminalError,
    isResidentialAddressError,
    isRiskPhoneConfirmationContingency,
    isWorldReadyIdentityDocumentError,
    message,
  } = parseGqlError(err);
  const actions = appStore.getActions();
  const appState = appStore.getState();
  const { form: formState } = appState;
  const { value: onboardOption } = formState.onboardOption;

  logger.info(`weasley_onboard_${onboardOption}_error`, {
    error: message,
    errorData,
    isOasError,
    isWorldReadyIdentityDocumentError,
  });

  if (isOasError) {
    if (errorData?.length) {
      const filteredErrors = errorData.filter(f => f);
      const { code: firstFieldError, type: addressTypeError } =
        filteredErrors[0] || {};

      if (FIELD_LEVEL_ERRORS.hasOwnProperty(firstFieldError)) {
        const addressType = addressTypeError || ADDRESS_TYPES.BILLING_ADDRESS;
        const addressFieldsRegexp = new RegExp(
          'city|line1|line2|postalCode|state',
        );

        // To consolidate the specific errorFlags on each forEach's iteration
        let consolidatedErrorFlags = {};
        filteredErrors.forEach(({ code, index }) => {
          const {
            field,
            payload,
            errorFlags = {},
          } = getErrorFieldAndPayload({
            addressType,
            code,
            formState,
            index,
          });

          // Acumulate current iteration flags in one obj {} (eg: hasCRSErrors)
          consolidatedErrorFlags = {
            ...consolidatedErrorFlags,
            ...errorFlags,
          };

          if (!formState[field] && !formState[addressType][field]) {
            return;
          }

          atomics.markProblem({
            context: code,
            domain: atomicsDomain,
            reason: atomicsErrorReason.user,
            status: atomicsStatus.error,
          });

          const isAddressField = addressFieldsRegexp.test(field);
          const action = isAddressField
            ? 'updateAddressField'
            : 'updateNonAddressField';

          actions.form[action]({
            ...(isAddressField && { addressType }),
            field,
            payload,
          });
        });

        // Specific actions: (eg: CRS, Residential Address is PO Box)
        triggerErrorBasedSpecificActions({
          actions,
          appState,
          errors: filteredErrors,
          sumErrorFlags: consolidatedErrorFlags,
        });

        // Log FPTI for unmapped tax residency server error (when all TIN fields are highlighted)
        // TEMPORARY SOLUTION: This hotfix highlights all TIN fields when backend returns
        // INVALID_IDENTITY_DOCUMENT_VALUE but doesn't specify which field is invalid.
        // TODO: DETXOCOMPL-43 Remove this workaround once backend provides field-level
        // error mapping.
        if (consolidatedErrorFlags.isUnmappedServerError) {
          const { crsTaxDetails, nationality } = formState;
          const onboardCountry = nationality?.value;
          const taxResidenciesDeclared =
            crsTaxDetails?.value
              ?.map(detail => detail.countryCode?.value)
              .join(', ') || '';

          logger.fpti('weasley_all_tin_fields_server_error', {
            onboard_country: onboardCountry,
            state_name: 'crs_section',
            tax_residencies_declared: taxResidenciesDeclared,
            transition_name: 'process_tax_residency_server_validation',
          });
        }

        CI.endCurrent(CI.Status.Error, CI.ErrorReason.User);
        return focusOnFirstError(appStore.getState().form);
        // For some OAS errors we show a page level error
      } else if (PAGE_LEVEL_ERRORS.hasOwnProperty(firstFieldError)) {
        error.message = firstFieldError;
      } else {
        // Adding this log for the time being so we can keep track of
        // any OAS errors that we need to map
        error.message = 'UNMAPPED_OAS_ERROR';
        logger.warn('weasley_unmapped_oas_error', {
          code: `${error.message}_${firstFieldError}`,
          error: message,
        });
      }
    } else {
      error.message = 'UNMAPPED_OAS_ERROR';
      // Adding this log to track OAS errors we need to map
      logger.warn('weasley_unmapped_oas_error', {
        code: error.message,
        error: message,
      });
    }
  }

  // IL AML identity-document errors - separate from isOasError since these
  // codes are unambiguous and don't need FIELD_LEVEL_ERRORS' field/index inference.
  if (isWorldReadyIdentityDocumentError) {
    const filteredErrors = (errorData || []).filter(f => f);
    const { code: firstFieldError } = filteredErrors[0] || {};
    const isKnownIdentityDocumentCode =
      firstFieldError === 'INVALID_NATIONAL_ID' ||
      firstFieldError === 'INVALID_PASSPORT';

    if (
      filteredErrors.length &&
      isKnownIdentityDocumentCode &&
      features.isActive('isILAMLEnabled')
    ) {
      const { field, payload } =
        getWorldReadyIdentityDocumentErrorPayload(firstFieldError);

      atomics.markProblem({
        context: firstFieldError,
        domain: atomicsDomain,
        reason: atomicsErrorReason.user,
        status: atomicsStatus.error,
      });

      actions.form.updateNonAddressField({ field, payload });

      CI.endCurrent(CI.Status.Error, CI.ErrorReason.User);
      return focusOnFirstError(appStore.getState().form);
    }

    // Defensive fallback for an unexpected code or flag state - shouldn't happen.
    error.message = 'UNMAPPED_IDENTITY_DOCUMENT_ERROR';
    logger.warn('weasley_unmapped_identity_document_error', {
      code: `${error.message}_${firstFieldError}`,
      error: message,
    });
  }

  if (isFiConfirmationContingency) {
    if (errorData?.confirmationMethod === 'ONE_TIME_PASSWORD_CONFIRMATION') {
      actions.api.setCupOtpFundingInstrumentId(errorData.fundingInstrumentId);
      // encryptedCardLoginAccountNumber only exists for guest transactions
      // which have a card linked login account number.
      errorData.encryptedCardLoginAccountNumber &&
        actions.api.setCupOtpEncryptedCardLoginAccountNumber(
          errorData.encryptedCardLoginAccountNumber,
        );
      return actions.ui.setIsOneTimePasswordConfirmationOpen(true);
    } else if (
      errorData?.confimationMethod === 'ONLINE_SECURE_AUTHENTICATION'
    ) {
      logger.info('threeds_2_add_card_contingency_error_thrown');
      return threeDSHandler({
        is3DSecureRequired: true,
        threeDS2ContingencyData: { ...errorData },
      });
    }
  }

  if (isResidentialAddressError) {
    return handleResidentialAddressContingency(errorData);
  }

  if (message === 'ADDRESS_WORLD_READY_VALIDATION_ERROR') {
    const { isWPS } = getCheckoutSessionFlags();
    const integrationType = isWPS ? 'WPS' : 'EC';
    logger.fpti('weasley_poboxvalidation_ineligible_errorshown', {
      context_id: getToken(),
      context_type: `${integrationType}-TOKEN`,
      state_name: 'billing_poboxvalidation',
      transition_name: 'ineligible_pobox_errorshown',
    });
    return handlePOBoxLine1AddressContingency(errorData);
  }

  if (isRiskPhoneConfirmationContingency) {
    logger.info('weasley_phone_confirmation_contingency_triggered', {
      action: 'would_show_phone_confirmation_modal',
      disabled: true,
    });
    actions.ui.setIsRiskPhoneConfirmation(true);
    actions.ui.toggleIsPhoneConfirmationOpen(true);
    return;
  }

  if (isOpenBankingTerminalError) {
    const fundingInstrumentId = errorData?.fundingInstrumentId;
    logger.error('open_banking_cookieless_user', {
      fundingInstrumentId,
    });
    logger.fpti('GUEST_OPEN_BANKING_CONTINGENCY_COOKIELESS_ERROR', {
      funding_instrument_id: fundingInstrumentId,
      state_name: 'open_banking',
      transition_name: 'open_banking_contingency_cookieless_error',
    });
    actions.ui.setPageWarningMessage('pageLevelError.cannotAddBank');
    actions.ui.toggleIsFormSubmitting(false);
    window.scrollTo(0, 0);
    return;
  }

  handleApiError(error);
};

export {
  handleResidentialAddressContingency,
  handlePOBoxLine1AddressContingency,
  onboardErrorHandler,
};
